import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { FacilityOption, pickedFacility } from './constants';

// The mapping step's review body: a facility the portal named has no saved mapping for this
// client, or the run collected no facility at all. Either way the answer posts as
// `facilityResolutions` — CollectedFacility entries, the same shape retrieval sends — and the
// api re-resolves from them, so the facilities it commits never come off this request directly.
//
// Two callers now (OLIO-3022): the step view's card, which owns its own Save, and the task
// slideout's Facility section, where the answer is one part of a single gesture that commits
// everything at once. So the picks are LIFTABLE — pass `picks` + `onPicksChange` and this renders
// the same interface with no button of its own. Uncontrolled it behaves exactly as before.

// Action-output blobs are camelCased by the API middleware (see eligibilityData.ts).
interface MappedEntry {
  externalValue: string;
  mappedValue: string;
}

interface MappingResult {
  unmapped?: string[];
  mapped?: MappedEntry[];
  options?: FacilityOption[];
}

// What the person has picked so far, in both shapes this interface can be in: one pick per
// portal string it could not map, or a column of outright picks when it collected nothing.
export interface FacilityPicks {
  byExternal: Record<string, FacilityOption | null>;
  direct: (FacilityOption | null)[];
}

export const NO_FACILITY_PICKS: FacilityPicks = { byExternal: {}, direct: [null] };

export interface FacilityMappingState {
  // `unmapped` asks the person to resolve strings the portal named; `direct` asks for facilities
  // outright, because the run collected none. The split is decided server-side by whether the
  // client's mapping bag left anything unresolved — never by the retrieval mode.
  mode: 'unmapped' | 'direct';
  unmapped: string[];
  mapped: MappedEntry[];
  options: FacilityOption[];
}

// The staged mapping result, read off the action. The options are the CLIENT's provider groups,
// not the acting user's allowed facilities — a mapped facility may legitimately be outside the
// latter, which is why this list comes from the server rather than from useAllowedFacilities.
export function facilityMappingState(action: WorkflowAction): FacilityMappingState {
  const raw = action.outputFor('facility_mapping_result');
  const result: MappingResult = raw && typeof raw === 'object' ? (raw as MappingResult) : {};
  const unmapped = Array.isArray(result.unmapped) ? result.unmapped : [];

  return {
    mode: unmapped.length > 0 ? 'unmapped' : 'direct',
    unmapped,
    mapped: Array.isArray(result.mapped) ? result.mapped : [],
    options: Array.isArray(result.options) ? result.options : [],
  };
}

// The `facilityResolutions` answer. A resolved portal string carries both halves, so the client
// keeps the mapping; an outright pick carries only the facility — the api rejects a pick naming
// a string this run never collected, so the two shapes are not interchangeable.
export function facilityResolutions(state: FacilityMappingState, picks: FacilityPicks): unknown[] {
  if (state.mode === 'unmapped') {
    return state.unmapped
      .filter((name) => picks.byExternal[name])
      .map((name) => ({ externalValue: name, mappedValue: picks.byExternal[name]!.id }));
  }
  return picks.direct.filter((pick): pick is FacilityOption => !!pick).map((pick) => pickedFacility(pick.id));
}

// Every unmapped string needs a pick before the step may pass; a direct pick needs at least one.
export function facilityPicksComplete(state: FacilityMappingState, picks: FacilityPicks): boolean {
  if (state.mode === 'unmapped')
    return state.unmapped.length > 0 && state.unmapped.every((name) => picks.byExternal[name]);
  return picks.direct.some(Boolean);
}

interface Props {
  action: WorkflowAction;
  // Uncontrolled (the step view): this component owns the picks and its own Save button.
  // Controlled (the task slideout): the picks live above and the save is somebody else's.
  picks?: FacilityPicks;
  onPicksChange?: (picks: FacilityPicks) => void;
  onComplete?: (actionId: string, outputs: Record<string, unknown>) => unknown;
  isCompleting?: boolean;
}

const optionLabel = (option: FacilityOption) => option.name;

export default function FacilityMappingReview({ action, picks, onPicksChange, onComplete, isCompleting }: Props) {
  const [internal, setInternal] = useState<FacilityPicks>(NO_FACILITY_PICKS);
  const controlled = !!onPicksChange;
  const current = picks ?? internal;
  const setPicks = (next: FacilityPicks) => (onPicksChange ? onPicksChange(next) : setInternal(next));

  const state = facilityMappingState(action);
  const ready = facilityPicksComplete(state, current) && !isCompleting;
  const save = () => onComplete?.(action.id, { facilityResolutions: facilityResolutions(state, current) });

  const saveButton = controlled ? null : (
    <div>
      <Button size='small' loading={isCompleting} disabled={!ready} onClick={save} data-testid='facility-mapping-save'>
        Save
      </Button>
    </div>
  );

  if (state.mode === 'unmapped') {
    return (
      <Wrapper data-testid='facility-mapping-review'>
        <div>
          <BodySmallBold>
            {state.unmapped.length} facilit{state.unmapped.length === 1 ? 'y' : 'ies'} could not be mapped
          </BodySmallBold>
          <Explanation>
            Pick the Olio facility each portal name refers to. The mapping is saved for this portal, so future referrals
            naming the same facility pass through automatically.
          </Explanation>
        </div>
        <UnmappedRows state={state} picks={current} setPicks={setPicks} />
        {saveButton}
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid='facility-mapping-direct-pick'>
      <div>
        <BodySmallBold>The portal named no facilities</BodySmallBold>
        <Explanation>Pick the facility (or facilities) this referral is for.</Explanation>
      </div>
      <DirectPick state={state} picks={current} setPicks={setPicks} quiet={controlled} />
      {saveButton}
    </Wrapper>
  );
}

interface RowProps {
  state: FacilityMappingState;
  picks: FacilityPicks;
  setPicks: (picks: FacilityPicks) => void;
}

// A string paired with a facility is a mapping the client keeps, so the next run naming it
// passes untouched.
function UnmappedRows({ state, picks, setPicks }: RowProps) {
  const nameFor = (mappedValue: string) =>
    state.options.find((option) => option.id === mappedValue)?.name ?? mappedValue;

  return (
    <Rows>
      {state.unmapped.map((name) => (
        <Row key={name} data-testid={`facility-mapping-row-${name}`}>
          <ExternalName>{name}</ExternalName>
          <SelectCell>
            <Select<FacilityOption>
              inputId={`facility-mapping-pick-${name}`}
              placeholder='Select a facility'
              options={state.options}
              value={picks.byExternal[name] ?? null}
              onChange={(option) => setPicks({ ...picks, byExternal: { ...picks.byExternal, [name]: option } })}
              menuPortalTarget={document.body}
              getOptionValue={(option) => option.id}
              getOptionLabel={optionLabel}
            />
          </SelectCell>
        </Row>
      ))}
      {state.mapped.map((entry) => (
        <Row key={entry.externalValue} data-testid={`facility-mapped-row-${entry.externalValue}`}>
          <ExternalName>{entry.externalValue}</ExternalName>
          <MappedName>{nameFor(entry.mappedValue)}</MappedName>
        </Row>
      ))}
    </Rows>
  );
}

// Nothing was collected, so there is no string to map and the person's picks are the whole
// answer — facilities on their own, no mapping saved. Still n-ary: a referral can name several.
// `quiet` is the slideout's stance on several facilities: the control still handles N, but the
// affordance announcing it is gone — a trailing row appears once the last one is filled instead.
function DirectPick({ state, picks, setPicks, quiet = false }: RowProps & { quiet?: boolean }) {
  const chosen = picks.direct.filter((pick): pick is FacilityOption => !!pick);
  const rows = quiet && picks.direct[picks.direct.length - 1] ? [...picks.direct, null] : picks.direct;

  const setRow = (index: number, option: FacilityOption | null) => {
    const next = rows.map((row, i) => (i === index ? option : row));
    // The trailing trim exists for `quiet`'s auto-appended row; in the step view the person
    // added that empty row themselves with `+ Add facility`, and editing an earlier row must
    // not silently take it back.
    if (quiet) while (next.length > 1 && !next[next.length - 1]) next.pop();
    setPicks({ ...picks, direct: next });
  };

  return (
    <Rows>
      {rows.map((pick, index) => (
        // Index keys: rows are positional (no identity beyond their slot).
        <SelectCell key={index}>
          <Select<FacilityOption>
            inputId={`facility-mapping-direct-pick-${index}`}
            placeholder='Select a facility'
            options={state.options.filter(
              (option) => option.id === pick?.id || !chosen.some((entry) => entry.id === option.id)
            )}
            value={pick}
            onChange={(option) => setRow(index, option)}
            menuPortalTarget={document.body}
            getOptionValue={(option) => option.id}
            getOptionLabel={optionLabel}
          />
        </SelectCell>
      ))}
      {!quiet && (
        <AddLink
          type='button'
          onClick={() => setPicks({ ...picks, direct: [...picks.direct, null] })}
          data-testid='facility-mapping-direct-add'>
          + Add facility
        </AddLink>
      )}
    </Rows>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Explanation = styled(BodySmall)`
  color: ${colors.black50};
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ExternalName = styled(BodySmallBold)`
  flex: 0 0 220px;
  word-break: break-word;
`;

const MappedName = styled(BodySmall)`
  color: ${colors.black50};
`;

const SelectCell = styled.div`
  width: 320px;
`;

const AddLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-size: 12px;
  text-decoration: underline;
`;
